import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  VStack,
  IconButton,
  Input,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import type { DeferredDocument } from 'types/documents';

interface DocumentTableProps {
  documents: DeferredDocument[];
  uploadingDoc: string | null;
  fileInputRefs: React.MutableRefObject<{
    [key: string]: HTMLInputElement | null;
  }>;
  onUploadClick: (docKey: string) => void;
  onFileSelect: (
    event: React.ChangeEvent<HTMLInputElement>,
    doc: DeferredDocument,
  ) => void;
}

const DocumentTable = ({
  documents,
  uploadingDoc,
  fileInputRefs,
  onUploadClick,
  onFileSelect,
}: DocumentTableProps) => {
  const { colorMode } = useColorMode();

  const textColor = colorMode === 'light' ? 'secondaryGray.900' : 'white';
  const hoverBg = colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100';
  const borderColor = colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'received':
        return 'blue';
      case 'resolved':
        return 'green';
      case 'auto_archived':
        return 'gray';
      case 'cannot_obtain':
        return 'red';
      case 'stale_ignored':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending Upload';
      case 'received':
        return 'Received';
      case 'resolved':
        return 'Resolved';
      case 'auto_archived':
        return 'Archived';
      case 'cannot_obtain':
        return 'Cannot Obtain';
      case 'stale_ignored':
        return 'Stale (Auto-Ignored)';
      default:
        return status;
    }
  };

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Box overflowX="auto">
        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              <Th borderColor={borderColor} color="gray.400">
                <Text color="gray.400">Document Name</Text>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <Text color="gray.400">Status</Text>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <Text color="gray.400">Files Uploaded</Text>
              </Th>
              <Th
                borderColor="gray.200"
                color="gray.500"
                bg="gray.50"
                w="100px"
              >
                <Text color="gray.400">Actions</Text>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {documents.map((doc: DeferredDocument) => {
              const docKey = `${doc.validation_key}-${doc.item_key}`;
              const isUploading = uploadingDoc === docKey;
              const isPending = doc.status === 'pending';

              return (
                <Tr
                  key={docKey}
                  _hover={{
                    bg: hoverBg,
                  }}
                >
                  <Td>
                    {doc.validation_message ? (
                      <Tooltip
                        label={doc.validation_message}
                        placement="top"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                        p="8px 12px"
                        borderRadius="md"
                      >
                        <Text
                          color={textColor}
                          fontSize="sm"
                          fontWeight="500"
                          cursor="help"
                          borderBottom="1px dotted"
                          borderColor="gray.400"
                        >
                          {doc.text}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {doc.text}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={getStatusColor(doc.status)}
                      fontSize="xs"
                    >
                      {getStatusLabel(doc.status)}
                    </Badge>
                  </Td>
                  <Td>
                    {doc.files.length > 0 ? (
                      <VStack align="start" spacing="4px">
                        {doc.files.map((file: any) => (
                          <Text
                            key={file.id}
                            fontSize="xs"
                            color={textColor}
                            isTruncated
                            maxW="200px"
                          >
                            {file.name}
                          </Text>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="sm" color="gray.400">
                        No files
                      </Text>
                    )}
                  </Td>
                  <Td>
                    {isPending && (
                      <>
                        <Tooltip label="Upload document" hasArrow>
                          <IconButton
                            aria-label="Upload document"
                            icon={<ArrowUpIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            isLoading={isUploading}
                            onClick={() => onUploadClick(docKey)}
                          />
                        </Tooltip>
                        <Input
                          type="file"
                          display="none"
                          ref={(el) => (fileInputRefs.current[docKey] = el)}
                          onChange={(e) => onFileSelect(e, doc)}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        />
                      </>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
};

export default DocumentTable;
